import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, Upload, BookOpen, UserCheck, CalendarClock, Undo2, UserX, PenLine, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { PhoneDialog } from "@/components/phones/PhoneDialog";
import { PhoneImportDialog } from "@/components/phones/PhoneImportDialog";
import { PhoneRegister, download } from "@/components/phones/PhoneRegister";
import { SignDialog } from "@/components/phones/SignDialog";
import { PhoneView } from "@/components/phones/PhoneView";
import { SimsPanel } from "@/components/phones/SimsPanel";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { stationColor } from "@/lib/format";
import { useSort, SortTh } from "@/lib/sort";

const FILTERS = [["all", "Tutti"], ["LIBERO", "Liberi"], ["FISSO", "Fissi"], ["GIORNALIERO", "In uso oggi"]];
const BADGE = { LIBERO: ["Libero", "border-emerald-200 bg-emerald-50 text-emerald-700"], FISSO: ["Fisso", "border-sky-200 bg-sky-50 text-sky-700"], GIORNALIERO: ["Giornaliero", "border-amber-200 bg-amber-50 text-amber-700"] };
const who = (i) => i.fixed_assignee || i.daily?.assignee || "";

const Actions = ({ i, isAdmin, act, onView, onEdit, onRelease, onDelete }) => (
  <td className="whitespace-nowrap px-3 py-2 text-right">
    <Button size="sm" variant="ghost" title="Scheda e storico" onClick={() => onView(i.id)} data-testid={`phone-view-${i.id}`}><Eye className="h-4 w-4" /></Button>
    {i.state === "LIBERO" && <>
      <Button size="sm" variant="ghost" title="Consegna giornaliera" onClick={() => act("daily", i)} data-testid={`phone-daily-${i.id}`}><CalendarClock className="h-4 w-4" /></Button>
      <Button size="sm" variant="ghost" title="Assegnatario fisso" onClick={() => act("fixed", i)} data-testid={`phone-fixed-${i.id}`}><UserCheck className="h-4 w-4" /></Button>
    </>}
    {i.state === "GIORNALIERO" && <Button size="sm" variant="outline" onClick={() => act("return", i)} data-testid={`phone-return-${i.id}`}><Undo2 className="mr-1 h-3.5 w-3.5" /> Riconsegna</Button>}
    {i.state === "FISSO" && !i.fixed_signed && <Button size="sm" variant="outline" className="border-amber-300 text-amber-700" onClick={() => act("fixed", i)} data-testid={`phone-sign-fixed-${i.id}`}><PenLine className="mr-1 h-3.5 w-3.5" /> Firma</Button>}
    {i.state === "FISSO" && <Button size="sm" variant="ghost" title="Libera assegnatario fisso" onClick={() => onRelease(i)} data-testid={`phone-release-${i.id}`}><UserX className="h-4 w-4" /></Button>}
    <Button size="sm" variant="ghost" onClick={() => onEdit(i)} data-testid={`phone-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>
    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => onDelete(i)} data-testid={`phone-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>}
  </td>
);

export default function PhonesPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ items: [], summary: {} });
  const [editing, setEditing] = useState(null);
  const [action, setAction] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showImport, setShowImport] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [tick, setTick] = useState(0);
  const [viewing, setViewing] = useState(null);
  const [view, setView] = useState("phones");

  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const load = useCallback(() => { api.get("/phones", { params: { station_id: sid } }).then((r) => setData(r.data)); }, [sid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/stations").then((r) => setStations(r.data)); }, []);

  const items = useMemo(() => {
    const t = q.trim().toLowerCase().replace(/\s/g, "");
    return data.items.filter((i) => (filter === "all" || i.state === filter)
      && (!t || [i.imei, i.internal_number, i.brand, i.model, i.sim_imei, i.phone_number, who(i), i.station_name].some((x) => (x || "").toLowerCase().replace(/\s/g, "").includes(t))));
  }, [data.items, q, filter]);
  const { sorted, sort, toggle } = useSort(items, { assignee: who });
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;

  const release = async (i) => {
    if (!window.confirm(`Liberare il telefono da ${i.fixed_assignee}?`)) return;
    try { await api.delete(`/phones/${i.id}/fixed`); toast.success("Telefono liberato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (i) => {
    if (!window.confirm(`Eliminare il telefono ${i.imei} e il suo storico?`)) return;
    try { await api.delete(`/phones/${i.id}`); toast.success("Telefono eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const s = data.summary;
  const title = isAdmin ? (stationId === "all" ? "Telefoni" : `Telefoni ${stations.find((x) => x.id === stationId)?.name || ""}`) : `Telefoni ${stations[0]?.name || ""}`;

  return (
    <div className="fade-up">
      <PageHeader title={title} subtitle="Telefoni aziendali, SIM, assegnatari fissi e consegne giornaliere con firma.">
        {view === "phones" && <>
        <Button variant="outline" onClick={() => setShowRegister(true)} data-testid="phone-register-button"><BookOpen className="mr-2 h-4 w-4" /> Schedario</Button>
        <Button variant="outline" onClick={() => setShowImport(true)} data-testid="phone-import-button"><Upload className="mr-2 h-4 w-4" /> Importa Excel</Button>
        <Button variant="outline" onClick={() => download("/phones/export", { station_id: sid }, "telefoni.xlsx")} data-testid="phone-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid || "" })} data-testid="phone-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo telefono</Button>
        </>}
      </PageHeader>

      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="phone-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((st) => (
            <button key={st.id} onClick={() => setStationId(st.id)} data-testid={`phone-tab-${st.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === st.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {st.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, st.id) }} />}{st.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-5 inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="phone-view-tabs">
        {[["phones", "Telefoni"], ["sims", "SIM"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`phone-view-tab-${k}`}
            className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
      </div>

      {view === "sims" ? <SimsPanel sid={sid} stations={stations} isAdmin={isAdmin} onChanged={load} /> : <>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Telefoni totali" value={s.total ?? 0} accent="#0F172A" testId="phone-kpi-total" />
        <KpiCard label="Liberi" value={s.free ?? 0} accent="#059669" testId="phone-kpi-free" />
        <KpiCard label="Assegnatari fissi" value={s.fixed ?? 0} accent="#0284C7" testId="phone-kpi-fixed" />
        <KpiCard label="In uso giornaliero" value={s.daily ?? 0} accent="#D97706" testId="phone-kpi-daily" />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-1" data-testid="phone-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`phone-filter-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca IMEI, n. interno, numero, assegnatario..." className="pl-9" data-testid="phone-search-input" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="phone-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr><S k="imei">IMEI telefono</S><S k="internal_number">N. interno</S><S k="brand">Marca</S><S k="model">Modello</S><S k="sim_imei">IMEI SIM</S><S k="phone_number">Numero</S>
                <S k="station_name">Stazione</S><S k="state">Tipo</S><S k="assignee">Assegnatario</S><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50" data-testid={`phone-row-${i.id}`}>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 font-semibold"><button onClick={() => setViewing(i.id)} className="hover:underline" data-testid={`phone-imei-link-${i.id}`}>{i.imei}</button></td>
                  <td className="whitespace-nowrap px-3 py-2" data-testid={`phone-internal-${i.id}`}>{i.internal_number || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.brand || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.model || "—"}</td>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 text-xs">{i.sim_imei || "—"}</td>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2">{i.phone_number || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.station_name}</td>
                  <td className="px-3 py-2"><span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${BADGE[i.state][1]}`} data-testid={`phone-state-${i.id}`}>{BADGE[i.state][0]}</span></td>
                  <td className="whitespace-nowrap px-3 py-2" data-testid={`phone-assignee-${i.id}`}>{who(i) || <span className="text-slate-400">—</span>}</td>
                  <Actions i={i} isAdmin={isAdmin} act={(mode, phone) => setAction({ mode, phone })} onView={setViewing} onEdit={setEditing} onRelease={release} onDelete={remove} />
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500" data-testid="phone-empty">{q || filter !== "all" ? "Nessun telefono corrisponde ai filtri." : "Nessun telefono registrato."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      </>}
      <PhoneRegister open={showRegister} stationId={sid} refreshKey={tick} onClose={() => setShowRegister(false)}
        onReturn={(a) => setAction({ mode: "return", phone: { ...a, daily: { id: a.id, assignee: a.assignee } } })} />
      <SignDialog key={action ? `${action.mode}-${action.phone.id}` : "none"} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); setTick((x) => x + 1); load(); }} />
      <PhoneView key={`${viewing}-${tick}`} phoneId={viewing} onClose={() => setViewing(null)} />
      <PhoneImportDialog key={String(showImport)} open={showImport} stations={stations} isAdmin={isAdmin} defaultStation={sid} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
      <PhoneDialog item={editing} stations={stations} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
